namespace ui
{
	export namespace Form
	{

		export interface IBasicInputDescriptor extends ui.base.IBaseHTMLElementDescriptor
		{
			name?: string;
			disabled?: boolean;
			noautocomplete?: boolean;
			readonly?: boolean;
			required?: boolean;
			maxlength?: number;
			length?: number;
			control?: boolean;
			placeholder?: string;
		}

		export enum InputState
		{
			ENABLED,
			REQUIRED,
			DISABLED
		}

		export enum InputValueTypes
		{
			STRING,
			NUMBER,
			DATETIME,
			DATE,
			TIME,
			ARRAY,
			BOOL,
			FILE,
			ANY
		}

		export abstract class BasicInputField<
			T extends HTMLElement = HTMLElement,
			D extends IBasicInputDescriptor = IBasicInputDescriptor
		>
			extends ui.base.HTMLPresentation<T>
		{

			public		Default:			any;

			public readonly	Descriptor:		D;
			public readonly	Name:					string|undefined;

			protected	m_Base:				JQuery;
			protected	m_StaticAlt:	JQuery;
			protected	m_Static:			boolean;

			private		m_State:			InputState;
			private		m_Type:				InputValueTypes;

			public onSave(): void
			{
				this.Default	= this.val();
			}

			public isSame( input: BasicInputField<T> ): boolean
			{
				return this.UUID === input.UUID;
			}

			public fire( event: string ): void
			{
				this.m_Root.trigger( event );
			}

			public Initialize(): void
			{
				super.Initialize( this.Descriptor );

				if ( !( "control" in this.Descriptor ) || this.Descriptor.control )
				{
					this.m_Root.addClass( 'form-control' );
				}

				if ( this.Descriptor.name )
				{
					this.attr( 'name', this.Descriptor.name );
				}

				if ( this.Descriptor.readonly )
				{
					this.m_Root.prop( 'readonly', true );
				}

				if ( this.Descriptor.noautocomplete )
				{
					this.attr( 'autocomplete', 'off' );
				}

				if ( this.Descriptor.required )
				{
					this.State( InputState.REQUIRED );
				}

				if ( this.Descriptor.disabled )
				{
					this.State( InputState.DISABLED );
				}

				if ( this.Descriptor.placeholder )
				{
					this.attr( 'placeholder', this.Descriptor.placeholder );
				}

				this.append( this.m_StaticAlt );
			}

			public State( state?: InputState ): InputState|void
			{
				if ( state !== undefined )
				{
					this.m_State	= state;

					switch ( state )
					{
						case InputState.ENABLED:
							this.m_Root.prop( 'disabled', false );
							this.m_Root.prop( 'required', false );
							break;

						case InputState.DISABLED:
							this.m_Root.prop( 'disabled', true );
							this.m_Root.prop( 'required', false );
							break;

						case InputState.REQUIRED:
							this.m_Root.prop( 'disabled', false );
							this.m_Root.prop( 'required', true );
							break;
					}

					return;
				}

				return this.m_State;
			}

			public abstract reset(): void;

			public Input(): JQuery<HTMLElement>
			{
				return super.Root();
			}

			public isStatic(): boolean
			{
				return this.m_Static;
			}

			public Root(): JQuery<HTMLElement>
			{
				return this.Base();
			}

			public Base(): JQuery<HTMLElement>
			{
				return this.m_Base;
			}

			public rebuild( root: ui.base.BaseHTMLElement<HTMLElement>|JQuery<HTMLElement> ): BasicInputField<T>
			{
				this.appendTo( root );

				this.Initialize();

				return this;
			}

			public Static( is_static_now: boolean ): void
			{
				if ( is_static_now )
				{
					this.m_StaticAlt.removeClass( 'hidden' );
					this.m_Root.addClass( 'hidden' );

					this.m_StaticAlt.text( (this.val() || '' ).toString() );
				}
				else
				{
					this.m_Root.removeClass( 'hidden' );
					this.m_StaticAlt.addClass( 'hidden' );
				}

				this.m_Static	= is_static_now;
			}

			public CheckIfValid(): boolean
			{
				if ( this.required() )
				{
					const val = this.val();

					if ( val === null )
					{
						return false;
					}

					return val instanceof Array ? val.length > 0 : val.toString() !== '';
				}

				return true;
			}

			public val(): any
			{
				let val = this.m_Root.val();

				if ( typeof val === 'string' )
				{
					val = val.replace( /\r/g, '' );
				}
				else if ( val instanceof Array )
				{
					for ( const v in val )
					{
						if ( val[v] === undefined )
						{
							continue;
						}

						val[ v ] = val[ v ].replace( /\r/g, '' );
					}
				}

				return val !== undefined ? val : null;
			}

			public value( value: any ): BasicInputField<T>
			{
				let newValue;
				switch (typeof value)
				{
					case 'boolean':
						newValue = value ? 'true' : 'false';
						break;
					case 'undefined':
						newValue = '';
						break;
					default:
						newValue = value;
						break;
				}

				this.m_Root.val(newValue);
				this.m_StaticAlt.empty().append(newValue instanceof Array ? newValue.join( ', ' ) : newValue.toString() );

				return this;
			}

			public disabled(): boolean
			{
				return this.m_State === InputState.DISABLED;
			}

			public required(): boolean
			{
				return this.m_State === InputState.REQUIRED;
			}

			protected constructor( tagName: string, descriptor: D, type: InputValueTypes )
			{
				super( tagName );

				this.Descriptor		= descriptor;
				this.Name					= descriptor.name;

				this.m_Base				= ui.$$.div();
				this.m_State			= InputState.ENABLED;
				this.m_StaticAlt	= ui.$$.p().addClass( 'form-control-static hidden' );
				this.m_Type				= type;

				this.m_Static			= false;
				this.Default			= null;

				this.m_Root.appendTo( this.m_Base );
			}

		}

		export abstract class Input<D extends IBasicInputDescriptor = IBasicInputDescriptor>
			extends BasicInputField<HTMLInputElement, D>
		{
			public constructor( value: any, descriptor: D, type: string, valuetype: InputValueTypes )
			{
				super( 'input', descriptor, valuetype );

				// Some enhancements for a password field
				if (descriptor.attr && descriptor.attr.type) {
					this.m_Root.attr('type', descriptor.attr.type);
				} else {
					this.m_Root.attr( 'type', type );
				}
				if ( typeof value === 'boolean' )
				{
					if ( value ) {
						this.m_Root.prop( 'checked' );
					}
				}
				else
				{
					if ( valuetype !== InputValueTypes.FILE )
					{
						this.m_Root.val( value || null );
					}
				}
			}

			public reset(): void
			{
				this.value( this.Default );
			}

		}

		export class TextInput
			extends Input
		{
			public constructor( value: string|null, descriptor: IBasicInputDescriptor )
			{
				super( value, descriptor , 'text', InputValueTypes.STRING );
			}

			public value( val: string|number|null ): TextInput
			{
				return super.value( val );
			}

			public val(): string|null
			{
				return (this.m_Root.val() as string).toString() || null;
			}
		}

		export class HiddenTextInput
			extends Input
		{
			public constructor( value: string|number|null, name_: string )
			{
				super( value, { name: name_ } , 'hidden', InputValueTypes.STRING );
			}

			public Initialize(): void
			{
				super.Initialize();

				this.m_Root.removeClass( 'form-control' );
			}

			public value( val: string|number|null ): TextInput
			{
				return super.value( val );
			}

			public val(): string|null
			{
				return (this.m_Root.val() as string).toString() || null;
			}

			public reset(): HiddenTextInput { return this; }
			public Static(): HiddenTextInput { return this; }
		}

		export class HiddenNumberInput
			extends Input
		{
			public constructor( value: number|null, name_: string )
			{
				super( value, { name: name_ } , 'hidden', InputValueTypes.NUMBER );
			}

			public Initialize(): void
			{
				super.Initialize();

				this.m_Root.removeClass( 'form-control' );
			}

			public value( val: number|null ): HiddenNumberInput
			{
				return super.value( val ) as HiddenNumberInput;
			}

			public val(): number
			{
				return parseInt( this.m_Root.val() as string );
			}

			public reset(): HiddenNumberInput { return this; }
			public Static(): HiddenNumberInput { return this; }
		}

	}
}
